import { RING_COLORS, RING_INKS } from "../maps/tokens";
import { type Played, playCard } from "./card";
import { faces, rollTotal } from "./faces";
import { type RollData, rollSummary } from "./feed";
import { buildDieSvg, type DieFx } from "./die_svg";
import { CARD_FADE_MS, CARD_KEPT_MS, CARDS_KEPT, chooseSchedule, createLanes, THROWN_DICE, type ThrowSchedule } from "./landing";
import { reducedMotion, throwEnabled } from "./preferences";
import { type Face, type Thrown, throwDice, throwRoom } from "./throw";

const SUMMARIES_KEPT = 4;
const LANDING_EVENTS = ["start", "throw", "bounce", "land", "flip", "total", "skip", "done"];

export type Play = { key: string; own: boolean };
type Insets = { right: number; bottom: number };

// Where a roll plays: the stack its cards go into, the layer its dice fly over (none, and it only flips), the live
// region that speaks it and the card template `_dice_card_templates.html.erb` renders.
export type StageHost = {
	cards: HTMLElement;
	layer: HTMLElement | null;
	announcer: HTMLElement;
	cardTemplate: HTMLTemplateElement;
	// The dice bar the dice fly from and keep clear of.
	roller?: () => HTMLElement | null;
	// The table view has no Dados animados switch and always throws.
	alwaysThrow?: () => boolean;
	// What a docked panel covers of the stack's corner, or null for none.
	insets?: () => Insets | null;
	// Gives a focus left on a card that is about to go somewhere to stay.
	refocus?: () => void;
	// A focus on the skip button follows the card's own ✕ instead of going to `refocus`.
	focusDismiss?: boolean;
	// Speaks the roll's description and note after its summary, as the live sheet does for rolls that share a name.
	announceDetails?: boolean;
	// The table view's cards fade CARD_KEPT_MS after they land, since no one there taps them; elsewhere a card stays until
	// it is dismissed.
	fadeCards?: () => boolean;
	// "Limpar todas as rolagens", shown over the stack while it holds two cards or more.
	clearAllTemplate?: HTMLTemplateElement | null;
};

export function findTemplates(root: ParentNode) {
	return {
		cardTemplate: root.querySelector<HTMLTemplateElement>("template[data-dice-card-template]"),
		clearAllTemplate: root.querySelector<HTMLTemplateElement>("template[data-dice-clear-all-template]"),
	};
}

// The guardrails' tsconfig turns strictNullChecks off, so a plain `lane ===
// null` check does not narrow `chooseSchedule`'s result; a type guard does.
function isThrown(choice: ReturnType<typeof chooseSchedule>): choice is { lane: number; schedule: ThrowSchedule } {
	return choice.lane !== null;
}

// Plays rolls on cards in a stack, and on feed entries given to `play`, with the dice thrown over a layer. The Mapas
// board's `map-dice` and the live sheet's page both use it.
export function createStage(host: StageHost) {
	const { cards, layer, cardTemplate } = host;
	const plays = new Map<HTMLElement, Played>();
	// A landed card: its fade timer on the table view, and its dice, if any are still on the board.
	const linger = new Map<HTMLElement, { timer: ReturnType<typeof setTimeout> | null; thrown: Thrown | null }>();
	const fades = new Set<ReturnType<typeof setTimeout>>();
	const fading = new Set<Animation>();
	// The dice of rolls with no card, until they leave the layer, so a clear() also stops their timers.
	const loose = new Set<Thrown>();
	const lanes = createLanes();
	const clearAll = host.clearAllTemplate?.content.firstElementChild.cloneNode(true) as HTMLElement | undefined;
	clearAll?.addEventListener("click", () => {
		for (const card of stacked()) drop(card);
		tidy();
	});

	// The stack's cards, without the button over them.
	const stacked = () => [...cards.children].filter((element) => element !== clearAll) as HTMLElement[];

	// Shows "Limpar todas as rolagens" over two cards or more, and takes it away, with a focus on it, below that.
	function tidy() {
		if (!clearAll || host.fadeCards?.()) return;
		const many = stacked().length > 1;
		if (many && !clearAll.isConnected) {
			cards.append(clearAll);
			clearAll.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: CARD_FADE_MS });
		} else if (!many && clearAll.isConnected) {
			if (clearAll.contains(document.activeElement)) host.refocus?.();
			clearAll.remove();
		}
	}

	// A die of the card: the shaded die, a critical hit or miss lit as the thrown one is.
	function buildDie(sides: number, value: number, color: string, verdict: RollData["verdict"]) {
		const fx: DieFx | null =
			sides === 20 && Math.abs(value) === 20 && verdict === "critical_hit"
				? "critico"
				: sides === 20 && Math.abs(value) === 1 && verdict === "critical_miss"
					? "erro"
					: null;
		const die = document.createElement("span");
		die.className = "odo-dice-card__die";
		die.dataset.die = "";
		die.dataset.sides = String(sides);
		die.dataset.value = String(value);
		die.title = `d${sides}: ${value}`;
		die.append(buildDieSvg({ sides, value, color, fx }).svg);
		return die;
	}

	function buildCard(data: RollData) {
		const card = cardTemplate.content.firstElementChild.cloneNode(true) as HTMLElement;
		const part = (name: string) => card.querySelector<HTMLElement>(`[data-card-part="${name}"]`);
		for (const name of ["name", "notation", "description"] as const) {
			if (data[name]) part(name).textContent = data[name];
			else part(name).remove();
		}
		if (data.notation) part("notation").title = data.notation;
		// The dot carries the roller's initial, from a handle's name or a character's.
		if (data.name) part("dot").textContent = (data.name.replace(/^@/, "")[0] ?? "?").toUpperCase();
		else part("dot").remove();
		if (data.note) part("note").textContent = data.note;
		else part("note-row").remove();
		// The eye marks a roll only its roller sees; a posted one under "?????" has the masks instead.
		part(data.noteIcon === "masks" ? "note-eye" : "note-masks")?.remove();

		const color = RING_COLORS[data.color] ? data.color : "carmesim";

		const pair = faces(data.notation, data.result);
		const more = part("more");
		const modifier = card.querySelector<HTMLElement>("[data-roll-modifier]");
		if (pair) {
			const shown = pair.dice.slice(0, THROWN_DICE);
			more.before(...shown.map(([sides, value]) => buildDie(sides, value, color, data.verdict)));
			const rest = pair.dice.length - shown.length;
			if (rest) {
				more.textContent = (rest === 1 ? cardTemplate.dataset.moreOne : cardTemplate.dataset.moreOther).replace("%{count}", String(rest));
				more.title = data.result;
			} else more.remove();
			if (pair.modifier) modifier.textContent = `${pair.modifier > 0 ? "+" : ""}${pair.modifier}`;
			else modifier.remove();
		} else part("tiles").textContent = data.result;

		card.querySelector("[data-roll-total]").textContent = String(rollTotal(data.result));
		for (const badge of card.querySelectorAll<HTMLElement>("[data-badge]")) {
			if (badge.dataset.badge === data.verdict) badge.dataset.rollVerdictBadge = "";
			else badge.remove();
		}
		card.dataset.rollColor = color;
		if (data.verdict) card.dataset.rollVerdict = data.verdict;
		card.style.setProperty("--roll-color", RING_COLORS[color]);
		card.style.setProperty("--roll-ink", RING_INKS[color]);
		return card;
	}

	function summary(data: RollData, element: HTMLElement) {
		const badge = element.querySelector("[data-roll-verdict-badge]")?.textContent.trim();
		const details = host.announceDetails ? [data.description, data.note] : [];
		return [rollSummary(cardTemplate.dataset.summary, data), ...details, badge].filter(Boolean).join(". ");
	}

	function announce(text: string) {
		const line = document.createElement("p");
		line.textContent = text;
		host.announcer.append(line);
		while (host.announcer.childElementCount > SUMMARIES_KEPT) host.announcer.firstElementChild.remove();
	}

	// The board the dice may land on, and what they keep clear of, in the layer's pixels.
	function throwArea() {
		const box = layer.getBoundingClientRect();
		const within = (rect: DOMRect) =>
			rect?.width ? { left: rect.left - box.left, top: rect.top - box.top, right: rect.right - box.left, bottom: rect.bottom - box.top } : null;
		const roller = host.roller?.();
		const part = (name: string) => within(roller?.querySelector(`[data-dice-roller-target="${name}"]`)?.getBoundingClientRect());
		// The dice leave the tab, or the box's ✕ once it is open.
		const d20 = part("opener") ?? part("closer");
		return {
			width: layer.clientWidth,
			height: layer.clientHeight,
			origin: d20 && { x: (d20.left + d20.right) / 2, y: (d20.top + d20.bottom) / 2 },
			box: part("column"),
			bottomInset: host.insets?.()?.bottom ?? 0,
			// The dice match the cards, which the table view zooms to read from across the table.
			scale: Number(getComputedStyle(cards).zoom) || 1,
		};
	}

	function throwFor(
		element: HTMLElement,
		tiles: HTMLElement[],
		area: ReturnType<typeof throwArea>,
		lane: number,
		schedule: ThrowSchedule,
		detail: Play,
		onTap?: () => void,
		onGone?: () => void,
	) {
		const dice = tiles.map(
			(tile): Face => [Number(tile.dataset.sides), Number(tile.dataset.value ?? tile.querySelector("[data-die-value]")?.textContent.trim())],
		);
		return throwDice(layer, {
			faces: dice,
			color: element.dataset.rollColor || null,
			lane,
			origin: area.origin,
			box: area.box,
			bottomInset: area.bottomInset,
			scale: area.scale,
			schedule,
			target: element,
			detail,
			verdict: element.dataset.rollVerdict,
			onLanded: () => lanes.free(lane),
			// The table view's dice are for looking at.
			onTap: host.alwaysThrow?.() ? undefined : onTap,
			onGone,
		});
	}

	// The dice go up before the card plays, so each die lands before its tile
	// turns. Returns the thrown dice, if any, so a card can fade them with it once done.
	function play(element: HTMLElement, detail: Play, onTap?: () => void, onGone?: () => void): Thrown | null {
		if (element.dataset.state === "playing") return null;
		const tiles = [...element.querySelectorAll<HTMLElement>("[data-die-tile]"), ...element.querySelectorAll<HTMLElement>("[data-die]")];
		const area = layer && (host.alwaysThrow?.() || throwEnabled()) ? throwArea() : null;
		const choice = chooseSchedule(tiles.length, { reduced: reducedMotion(), throwing: Boolean(area && throwRoom(area)), lanes });
		const thrown = isThrown(choice) ? throwFor(element, tiles, area, choice.lane, choice.schedule, detail, onTap, onGone) : null;
		element.addEventListener("dice-landing:done", () => plays.delete(element), { once: true });
		const played = playCard(element, choice.schedule, detail);
		if (element.dataset.state !== "playing") {
			thrown?.clear();
			return null;
		}
		const cancel = () => {
			thrown?.clear();
			played.cancel();
		};
		plays.set(element, { skip: played.skip, cancel });
		return thrown;
	}

	// A card that landed stays until it is dismissed, and its dice fade on their own (`linger`): the "Pular animação"
	// button goes, since there is no more animation to skip, replaced by the accessible ✕ that dismisses the card.
	function landed(card: HTMLElement, thrown: Thrown | null) {
		const skip = card.querySelector<HTMLElement>('[data-card-part="skip"]');
		const dismissButton = card.querySelector<HTMLElement>('[data-card-part="dismiss"]');
		dismissButton?.classList.remove("hidden");
		// A focus left on the skip button would fall back to the body as it goes.
		if (skip?.contains(document.activeElement)) {
			if (host.focusDismiss) dismissButton?.focus();
			else host.refocus?.();
		}
		skip?.remove();
		thrown?.linger(CARD_KEPT_MS, CARD_FADE_MS);
		const timer = host.fadeCards?.() ? setTimeout(() => fade(card), CARD_KEPT_MS) : null;
		if (timer) fades.add(timer);
		linger.set(card, { timer, thrown });
	}

	// The table view's card has had its time: fade it, and any dice left, together. It stays kept while it fades, so a
	// drop meanwhile still clears its dice and hands its focus on.
	function fade(card: HTMLElement) {
		const entry = linger.get(card);
		if (entry) fades.delete(entry.timer);
		// A focus left inside the card, such as on its ✕, would fall back to the body as it goes.
		const leave = () => {
			linger.delete(card);
			if (card.contains(document.activeElement)) host.refocus?.();
			card.remove();
			tidy();
		};
		if (!card.isConnected || typeof card.animate !== "function") {
			entry?.thrown?.clear();
			leave();
			return;
		}
		const going = card.animate([{ opacity: 1 }, { opacity: 0 }], { duration: CARD_FADE_MS, fill: "forwards" });
		fading.add(going);
		entry?.thrown?.fadeOut(CARD_FADE_MS);
		const gone = () => {
			fading.delete(going);
			leave();
		};
		going.finished.then(gone, gone);
	}

	// Cancels a kept card's fade, if it has one, and clears its dice at once, false if it was never kept.
	function dismiss(card: HTMLElement): boolean {
		const entry = linger.get(card);
		if (!entry) return false;
		linger.delete(card);
		clearTimeout(entry.timer);
		fades.delete(entry.timer);
		entry.thrown?.clear();
		// A focus left on the card's ✕ would fall back to the body as the caller removes it.
		if (card.contains(document.activeElement)) host.refocus?.();
		return true;
	}

	function drop(card: HTMLElement) {
		plays.get(card)?.skip();
		dismiss(card);
		card.remove();
	}

	// The card goes into the stack before it plays: its start is dispatched at once.
	function show(data: RollData, detail: Play) {
		const card = buildCard(data);
		card.dataset.rollKey = detail.key;
		if (detail.own) card.dataset.own = "";
		const insets = host.insets?.();
		if (insets) {
			cards.style.marginRight = `${insets.right}px`;
			cards.style.marginBottom = `${insets.bottom}px`;
		}
		if (clearAll?.isConnected) clearAll.before(card);
		else cards.append(card);
		for (const old of stacked().slice(0, -CARDS_KEPT)) drop(old);
		tidy();
		announce(summary(data, card));
		// A tap on the card skips it while it plays and dismisses it once kept; a tap on its dice skips it too, but once
		// landed clears only the dice, which may hide the board, and the card keeps the result.
		card.addEventListener("click", () => {
			const played = plays.get(card);
			if (played) played.skip();
			else if (dismiss(card)) {
				card.remove();
				tidy();
			}
		});
		// thrown is read once play() returns: playCard's own instant mode aside,
		// dice-landing:done never fires before then.
		let thrown: Thrown | null = null;
		card.addEventListener("dice-landing:done", () => landed(card, thrown), { once: true });
		thrown = play(card, detail, () => {
			const played = plays.get(card);
			if (played) played.skip();
			else thrown?.clear();
		});
		return card;
	}

	// A roll with no card: one whose row the open panel draws on its own, or another player's on the board, whose row is in
	// the campaign's list. Its dice are thrown from a card that is never shown, and linger as a card's do. The card is
	// detached, so its dice-landing events are forwarded to the layer, where the board's listeners hear them.
	function throwOnly(data: RollData, detail: Play) {
		const card = buildCard(data);
		announce(summary(data, card));
		const stage = layer ?? cards;
		for (const name of LANDING_EVENTS) {
			card.addEventListener(`dice-landing:${name}`, (event: CustomEvent) =>
				stage.dispatchEvent(new CustomEvent(event.type, { bubbles: true, detail: event.detail })),
			);
		}
		let thrown: Thrown | null = null;
		let gone = false;
		card.addEventListener("dice-landing:done", () => thrown?.linger(CARD_KEPT_MS, CARD_FADE_MS), { once: true });
		thrown = play(
			card,
			detail,
			() => {
				const played = plays.get(card);
				if (played) played.skip();
				else thrown?.clear();
			},
			() => {
				gone = true;
				loose.delete(thrown);
			},
		);
		if (thrown && !gone) loose.add(thrown);
	}

	// A roll the server refused never lands: its own card goes, silently.
	function withdraw(key: string) {
		const card = stacked().findLast((element) => element.dataset.rollKey === key && "own" in element.dataset);
		if (!card) return;
		plays.get(card)?.cancel();
		plays.delete(card);
		dismiss(card);
		card.remove();
		tidy();
	}

	function skipAll() {
		for (const played of [...plays.values()]) played.skip();
	}

	const hidden = () => {
		if (document.hidden) skipAll();
	};
	document.addEventListener("visibilitychange", hidden);

	// The page owner, which Turbo may carry to the next page, keeps nothing of what was playing.
	function clear() {
		document.removeEventListener("visibilitychange", hidden);
		for (const timer of fades) clearTimeout(timer);
		fades.clear();
		for (const going of fading) going.cancel();
		fading.clear();
		for (const played of plays.values()) played.cancel();
		plays.clear();
		for (const { thrown } of linger.values()) thrown?.clear();
		linger.clear();
		for (const thrown of loose) thrown.clear();
		cards.replaceChildren();
		layer?.replaceChildren();
	}

	return { lanes, plays, show, play, throwOnly, announce, summary, withdraw, skipAll, clear };
}

export type Stage = ReturnType<typeof createStage>;
